import React, { useMemo, useState, useEffect } from 'react';
import { FaRocket, FaInfoCircle, FaCheck, FaUserPlus, FaGraduationCap, FaLightbulb, FaChalkboardTeacher, FaBrain, FaCalculator, FaRulerCombined, FaChartBar, FaFileAlt, FaUsers, FaChartLine, FaTrophy, FaPlayCircle, FaPenFancy, FaBook, FaBookOpen, FaSpinner } from 'react-icons/fa';
import Header from '../common/Header';
import Footer from '../common/Footer';

const HomePage = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  // إنشاء أيقونات عشوائية ثابتة باستخدام useMemo
  const backgroundIcons = useMemo(() => {
    const iconComponents = [
      FaBook, FaPenFancy, FaGraduationCap, FaLightbulb, FaChalkboardTeacher,
      FaBrain, FaCalculator, FaRulerCombined, FaChartBar, FaFileAlt,
      FaUsers, FaChartLine, FaTrophy, FaUserPlus, FaPlayCircle
    ];

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const numberOfIcons = isMobile ? 12 : 20;

    return Array.from({ length: numberOfIcons }, (_, index) => {
      const RandomIcon = iconComponents[Math.floor(Math.random() * iconComponents.length)];
      const colors = ['blue-400', 'pink-400', 'green-500', 'yellow-500', 'purple-500', 'red-400', 'orange-400', 'blue-500', 'teal-400', 'indigo-400'];
      const color = colors[Math.floor(Math.random() * colors.length)];

      return {
        id: index,
        Icon: RandomIcon,
        color,
        size: isMobile ? Math.random() * 20 + 20 : Math.random() * 30 + 30,
        left: Math.random() * 100,
        top: Math.random() * 100,
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.1 + 0.05 // شفافية بين 5% و 15%
      };
    });
  }, []);

  // دالة لجلب التعليقات من API
  const fetchRatings = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://question-manager-backend.onrender.com/api/rating');

      if (!response.ok) {
        throw new Error('فشل في جلب التعليقات');
      }

      const data = await response.json();
      setRatings(data.ratings || []);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setRatings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRatings();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 overflow-x-hidden flex flex-col" dir="rtl">
      {/* Header */}
      <Header />

      {/* Hero Section - المحتوى الرئيسي */}
      <section className="relative flex-grow flex items-center justify-center py-16 sm:py-20 lg:py-28 bg-gray-50 grid-pattern overflow-hidden">
        {/* دوائر إضاءة */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-600 opacity-5 rounded-full blur-3xl"></div>

        {/* أيقونات الخلفية العشوائية الثابتة */}
        {backgroundIcons.map((icon) => (
          <icon.Icon
            key={icon.id}
            className={`absolute text-${icon.color} transition-all duration-1000 pointer-events-none`}
            style={{
              left: `${icon.left}%`,
              top: `${icon.top}%`,
              fontSize: `${icon.size}px`,
              transform: `rotate(${icon.rotation}deg)`,
              opacity: icon.opacity,
            }}
          />
        ))}

        {/* محتوى الهيرو */}
        <div className="relative px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl w-full">
          <div className="max-w-3xl mx-auto text-center">
            {/* عنوان المنصة */}
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl leading-tight">
              منصة <span className="text-primary">نافس برو </span>
            </h1>

            <p className="max-w-2xl mx-auto mt-6 text-lg font-normal leading-8 text-gray-600">
              منصة نافس وجدت لتجعل مراجعة اختبارات نافس ممتعة وسهلة, نقدم مسابقات تفاعلية وأسئلة تدريبية تساعد الطلاب و الطالبات على مراجعة دروسهم, وقياس جاهزيتهم لاختبارات نافس الوطنية بطريقة مشوقة و محفزة.
            </p>

            {/* الأزرار */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
              <a
                href="/tests"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-primary rounded-xl hover:bg-primary/90 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <FaRocket className="ml-2" />
                ابدأ المراجعة الآن
              </a>

              <a
                href="/features"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary border-2 border-primary bg-white rounded-xl hover:bg-primary hover:text-slate-400 transition-all duration-300 shadow-sm"
              >
                <FaInfoCircle className="ml-2" />
                تعرف على المزيد
              </a>
            </div>

            {/* النقاط المميزة */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-sm font-medium text-gray-700">محتوى شهري جديد</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-sm font-medium text-gray-700">مجتمع تعليمي نشط</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                  <FaCheck className="text-green-600 text-xs" />
                </div>
                <span className="text-sm font-medium text-gray-700">مراجعات معتمدة</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - تم تعليقه حالياً */}
      {/* <Footer /> */}

      <style jsx>{`
        .grid-pattern {
          background-image:
            linear-gradient(rgba(50, 10, 107, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(50, 10, 107, 0.02) 1px, transparent 1px);
          background-size: 25px 25px;
          background-color: #f9fafb; /* نفس لون bg-gray-50 */
        }

        /* تحسينات للألوان والتباين */
        .text-primary {
          color: #320A6B;
        }

        .bg-primary {
          background-color: #320A6B;
        }

        .border-primary {
          border-color: #320A6B;
        }

        .text-dark-yellow {
          color: #B8860B;
        }

        .bg-dark-yellow {
          background-color: #B8860B;
        }

        /* تحسين التباين للنصوص على الخلفيات الملونة */
        .card-subtle {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.08);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .scroll-container {
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
        }

        .scroll-container::-webkit-scrollbar {
          display: none;
        }

        .scroll-item {
          scroll-snap-align: center;
        }
      `}</style>

      <style jsx global>{`
        body {
          font-family: 'Cairo', sans-serif;
          margin: 0;
          padding: 0;
        }

        html {
          scroll-behavior: smooth;
          height: 100%;
        }

        #__next {
          height: 100%;
        }

        /* تأكد من أن جميع النصوص مرئية */
        .text-gray-600 {
          color: #4b5563;
        }

        .text-gray-900 {
          color: #111827;
        }

        .text-white {
          color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default HomePage;